/**
 * What the pager's own report means.
 *
 * The reader's page is held twice: in the player's state, which is every screen and
 * the saved place are built from, and in the horizontal pager, which is what the reader
 * sees. The two are kept in step from both ends — the player asks the pager for a page,
 * and the pager reports the page it is showing — and the report is the only way a *drag*
 * gets back into the state, so it cannot simply be ignored.
 *
 * It also cannot simply be obeyed, and that is the whole reason this file exists. A
 * horizontal pager reports whatever is in the frame, and while it is *being moved* it
 * reports the pages along the way — or, on a slow frame, the page it was created on
 * before the move lands. Obeying one of those puts the reader back at the front of the
 * book, and that is exactly what a `resume` is made of: a learner presses Continue on
 * page 3 of a hundred-page book, the pager's own layout speaks first, and the read opens
 * on page 1 with the saved page thrown away. A screenshot of either state looks fine.
 *
 * So a seating the app asked for is *authoritative until it is answered*: while one is
 * outstanding, a report of a different page is the list moving rather than the reader.
 * Three rules keep that from becoming its own trap:
 *
 *  - **A report of the page that was asked for ends the request**, so the pager is the
 *    truth again the moment it has arrived.
 *  - **A page it reports in the meantime is corrected, not merely ignored.** Leaving the
 *    list where it wandered would put the screen and the saved place into disagreement
 *    for as long as nothing else moved — the reader looking at one page while the book
 *    believed they were on another — so the seating is asked for again.
 *  - **A request expires** (`SEAT_GRACE_MS`). A seating that never arrives — a page the
 *    pager cannot reach, a frame that resized mid-way — must not be able to speak for the
 *    reader for ever, and after the grace the list wins, which is at worst a page the
 *    reader can see.
 *
 * Pure, and about three numbers and a clock, so the race can be tested without a device,
 * a page turn or a slow frame.
 */

/**
 * How long a seating the app asked for may speak for the pager.
 *
 * Generous on purpose. It is not the length of a scroll: it is how long a *stale* report
 * about that scroll may keep arriving, which on a busy device is a frame to a few hundred
 * milliseconds. It is also how long a drag can be overruled, and that is bounded by the
 * seating it guards — an opening is instant, and the reader's finger is on the Continue
 * button while it happens, so there is no drag in flight to overrule.
 */
export const SEAT_GRACE_MS = 1000

/** A page the app moved the reader to, and how long it may stand for. */
export type SeatRequest = {
  /** The page asked for. Always a page the book has (see the caller). */
  index: number
  /** When the request stops speaking for the pager, in `Date.now()` terms. */
  until: number
}

export type PagerReading = {
  /**
   * The request to keep.
   *
   * `null` once the pager has answered, or once the request has expired — either way the
   * pager is the truth from then on.
   */
  requested: SeatRequest | null
  /**
   * The page the reader's own movement put them on, or `null` when nothing moved.
   *
   * The one case that has to be obeyed: nobody asked the pager for this page, so the
   * reader is the one who went there.
   */
  follow: number | null
  /**
   * The page to put the list back on, when it has moved off the one asked for.
   *
   * Not mere bookkeeping: without it the pager is left showing a page the book does not
   * think the reader is on, and the two only agree again if something happens to move
   * one of them.
   */
  reseat: number | null
}

const NOTHING: Omit<PagerReading, 'requested'> = {
  follow: null,
  reseat: null,
}

/**
 * Read one report from the pager.
 *
 * @param index The page the pager says it is showing, or `null` when it has nothing to
 *   report — a list that has mounted but not laid out yet, which is not a page the reader
 *   is on.
 * @param requested The seating that is still outstanding, if any.
 * @param current The page the player believes the reader is on.
 * @param now `Date.now()` at the moment of the report.
 */
export function readPagerReport({
  index,
  requested,
  current,
  now,
}: {
  index: number | null
  requested: SeatRequest | null
  current: number
  now: number
}): PagerReading {
  // Nothing to say: no layout yet, so no page to believe or disbelieve.
  if (index == null) return {requested, ...NOTHING}

  if (requested && now < requested.until) {
    return index === requested.index
      ? // Arrived. The request has done its job and the pager owns the place again.
        {requested: null, ...NOTHING}
      : // Still on its way: this report is the list moving, not the reader — so the
        // reader's page stands, and the list is put back on it.
        {requested, follow: null, reseat: requested.index}
  }

  // Either there was no request, or it has expired — and an expired one is spent, so a
  // report that disagrees is the pager's to keep.
  return {
    requested: null,
    follow: index === current ? null : index,
    reseat: null,
  }
}
